import {useCallback, useEffect, useRef, useState} from 'react'

export type RefreshCallback = () => void | Promise<any>
export type RefreshOptions = {
  interval?: number
  onRefresh?: RefreshCallback
}

function isPageVisible() {
  return document.visibilityState === 'visible'
}

function noop() {
  return undefined
}

/**
 * Периодический вызов функции для рефреша чего-нибудь.
 * Затихает, пока страница не видна в браузере и сразу восстанавливается когда её опять видно.
 * @param callback - функция, которую надо периодически вызывать
 * @param options
 */
function useRefresh(callback?: RefreshCallback, options: RefreshOptions = {}) {
  const [lastRefreshed, setLastRefreshed] = useState(Date.now())
  const visibleRef = useRef(true)
  const onRefresh = options.onRefresh || noop
  const interval = options.interval
  const onRefreshSuccess = useCallback(() => {
    onRefresh()
    setLastRefreshed(Date.now())
  }, [onRefresh])
  const doRefresh = useCallback(() => {
    if (!visibleRef.current || !interval) {
      return
    }
    if (lastRefreshed > Date.now() - interval) {
      setLastRefreshed(Date.now())
      return
    }
    const result = callback && callback()
    if (result && result.then) {
      result.then(() => onRefreshSuccess())
    } else {
      onRefreshSuccess()
    }
  }, [lastRefreshed, interval, callback, onRefreshSuccess])

  useEffect(() => {
    const onDocumentVisibilityChange = () => {
      visibleRef.current = isPageVisible()
      if (visibleRef.current) {
        doRefresh()
      }
    }
    document.addEventListener('visibilitychange', onDocumentVisibilityChange)
    return () => {
      document.removeEventListener(
        'visibilitychange',
        onDocumentVisibilityChange,
      )
    }
  }, [doRefresh])

  useEffect(() => {
    if (!visibleRef.current || !callback) {
      return
    }
    const timeout = setTimeout(doRefresh, interval)
    return () => {
      timeout && clearTimeout(timeout)
    }
  }, [doRefresh, interval, callback])
}

export default useRefresh
